import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { RefreshCw, AlertCircle, ChevronUp, ChevronDown, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { useBuildings } from 'contexts/BuildingsContext';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Building } from 'services/api/buildings';

// Format Firestore Timestamp ({ _seconds, _nanoseconds }) or fallback types
const formatDate = (timestamp: any) => {
  if (!timestamp) return 'N/A';
  
  try {
    let date: Date;
    
    // Handle Firestore timestamp format {_seconds, _nanoseconds}
    if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      date = new Date(timestamp._seconds * 1000);
    }
    // Handle Firestore Timestamp object
    else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    }
    // Handle regular Date object or timestamp number
    else if (timestamp instanceof Date) {
      date = timestamp;
    }
    else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    }
    // Handle ISO string
    else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    }
    else {
      return 'N/A';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return 'N/A';
  }
};

const formatShortDate = (timestamp: any) => {
  if (!timestamp) return 'N/A';
  try {
    let date: Date;
    if (timestamp._seconds && typeof timestamp._seconds === 'number') {
      date = new Date(timestamp._seconds * 1000);
    } else if (timestamp.toDate && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } else if (timestamp instanceof Date) {
      date = timestamp;
    } else if (typeof timestamp === 'number') {
      date = new Date(timestamp);
    } else if (typeof timestamp === 'string') {
      date = new Date(timestamp);
    } else {
      return 'N/A';
    }
    if (isNaN(date.getTime())) return 'N/A';
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  } catch {
    return 'N/A';
  }
};

// Sortable table header component
const SortableHeader = ({ 
  field, 
  children, 
  currentSortField, 
  currentSortDirection, 
  onSort 
}: {
  field: keyof Building;
  children: React.ReactNode;
  currentSortField: keyof Building | null;
  currentSortDirection: 'asc' | 'desc';
  onSort: (field: keyof Building) => void;
}) => {
  const isActive = currentSortField === field;
  
  return (
    <TableHead 
      className="cursor-pointer hover:bg-gray-100 transition-colors text-center"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center justify-center gap-1">
        {children}
        {isActive ? (
          currentSortDirection === 'asc' ? (
            <ChevronUp className="h-4 w-4 text-gray-600" />
          ) : (
            <ChevronDown className="h-4 w-4 text-gray-600" />
          )
        ) : (
          <div className="h-4 w-4" />
        )}
      </div>
    </TableHead>
  );
};

const MyBuildingsPage = () => {
  const { 
    paginatedBuildings, 
    loading, 
    error, 
    refreshBuildings,
    currentPage,
    pageSize,
    totalPages,
    setCurrentPage,
    setPageSize,
    sortField,
    sortDirection,
    setSortField,
    setSortDirection,
    searchTerm,
    setSearchTerm,
    totalFilteredCount
  } = useBuildings();

  const handleSort = (field: keyof Building) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  if (loading) {
    return (
      <div className="p-8 pt-2">
        <Card>
          <CardHeader>
            <CardTitle>I miei stabili</CardTitle>
            <CardDescription>Loading buildings...</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8 pt-2">
        <Card>
          <CardHeader>
            <CardTitle>I miei stabili</CardTitle>
            <CardDescription>Error loading buildings</CardDescription>
          </CardHeader>
          <CardContent>
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                {error}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="ml-2"
                  onClick={refreshBuildings}
                >
                  Retry
                </Button>
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-8 pt-2">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Lista dei {totalFilteredCount} stabili amministrati</CardTitle>
              <CardDescription>Esempio: Lista dei {totalFilteredCount} stabili amministrati.</CardDescription>
            </div>
            <Button onClick={refreshBuildings} variant="outline" size="sm">
              <RefreshCw className="h-4 w-4 mr-2" />
              Aggiorna
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search and Controls */}
          <div className="flex items-center gap-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Cerca stabile..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">Mostra</span>
              <Select value={String(pageSize)} onValueChange={(value) => setPageSize(Number(value))}>
                <SelectTrigger className="w-20">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Table */}
          {paginatedBuildings.length > 0 ? (
            <Table className="table-fixed">
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <SortableHeader 
                    field="displayName" 
                    currentSortField={sortField} 
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    <span className="block text-center w-full font-semibold text-gray-900">Stabile</span>
                  </SortableHeader>
                  <TableHead className="text-center w-1/5 font-semibold text-gray-900">Indirizzo</TableHead>
                  <TableHead className="text-center w-1/6 font-semibold text-gray-900">Città</TableHead>
                  <TableHead className="text-center w-1/6 font-semibold text-gray-900">Ultimo download</TableHead>
                  <TableHead className="text-center w-1/5 font-semibold text-gray-900">Periodo bonifici</TableHead>
                  <SortableHeader 
                    field="createdAt" 
                    currentSortField={sortField} 
                    currentSortDirection={sortDirection}
                    onSort={handleSort}
                  >
                    <span className="block text-center w-full font-semibold text-gray-900">Creazione</span>
                  </SortableHeader>
                  <TableHead className="text-center w-24 font-semibold text-gray-900">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {paginatedBuildings.map((building) => (
                  <TableRow key={building.id} className="hover:bg-gray-50">
                    <TableCell className="text-left whitespace-normal break-words text-gray-700">
                      {`${building.name} - ${building.descri || ''}`.trim()}
                    </TableCell>
                    <TableCell className="text-center whitespace-normal break-words text-gray-700">{(building as any).indiri || building.address || 'N/A'}</TableCell>
                    <TableCell className="text-center whitespace-normal break-words text-gray-700">{(building as any).citind || 'N/A'}</TableCell>
                    <TableCell className="text-center whitespace-normal break-words text-gray-700">{formatShortDate((building as any).last_versamenti_csv_download_at)}</TableCell>
                    <TableCell className="text-center whitespace-normal break-words text-gray-700">{`${formatShortDate((building as any).last_bank_file_tx_min_date)} – ${formatShortDate((building as any).last_bank_file_tx_max_date)}`}</TableCell>
                    <TableCell className="text-center text-gray-700">
                      {formatDate(building.createdAt)}
                    </TableCell>
                    <TableCell className="text-center">
                      {/* Read-only status display */}
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        (building as any).is_enabled !== false 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {(building as any).is_enabled !== false ? '✓' : '✗'}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">
                {searchTerm ? 'No buildings found matching your search.' : 'No buildings found in your organization.'}
              </p>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted-foreground">
                Da {((currentPage - 1) * pageSize) + 1} a {Math.min(currentPage * pageSize, totalFilteredCount)} di {totalFilteredCount} stabili
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Precedente
                </Button>
                
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum: number;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }
                    
                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? "default" : "outline"}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="w-8 h-8 p-0"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Successivo
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyBuildingsPage; 